import { useId } from 'react'
import { createPortal } from 'react-dom'
import { CheckIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import { useDialogFocus } from '@/hooks/useDialogFocus'
import type { ServerCancellationReceipt } from '@/lib/serverCancellation'
import './ServerCancellationProgressModal.css'

export interface ServerCancellationProgressModalProps {
  serverName: string
  state: 'submitting' | 'accepted' | 'unconfirmed'
  receipt: ServerCancellationReceipt | null
  statusUnavailable?: boolean
  onClose: () => void
  onRetry: () => void
}

function LandingScene({ accepted, submitting }: { accepted: boolean; submitting: boolean }) {
  const id = useId().replace(/:/g, '')
  return <div className="server-cancellation-scene" aria-hidden="true" data-accepted={accepted} data-submitting={submitting}>
    <svg viewBox="0 0 460 192" focusable="false">
      <defs>
        <radialGradient id={`${id}-halo`}><stop stopColor="#9b83ef" stopOpacity=".3" /><stop offset="1" stopColor="#9b83ef" stopOpacity="0" /></radialGradient>
        <linearGradient id={`${id}-body`} x1="0" y1="0" x2="1" y2=".3"><stop stopColor="#b3a4dc" /><stop offset=".45" stopColor="#fff" /><stop offset="1" stopColor="#cabde7" /></linearGradient>
        <linearGradient id={`${id}-fin`} x1="0" y1="0" x2="1" y2="1"><stop stopColor="#c4b5fd" /><stop offset="1" stopColor="#7651b1" /></linearGradient>
        <linearGradient id={`${id}-flame`} x1="0" y1="0" x2="0" y2="1"><stop stopColor="#f3ddff" /><stop offset=".5" stopColor="#b69cff" /><stop offset="1" stopColor="#9572e5" stopOpacity="0" /></linearGradient>
      </defs>
      <ellipse cx="230" cy="135" rx="190" ry="115" fill={`url(#${id}-halo)`} />
      <g className="server-cancellation-stars" fill="currentColor">
        <circle cx="79" cy="46" r="1.5" /><circle cx="125" cy="92" r="1" /><circle cx="162" cy="27" r="1.5" />
        <circle cx="310" cy="30" r="1" /><circle cx="350" cy="89" r="1.5" /><circle cx="391" cy="46" r="1" />
        <path d="M97 112v8m-4-4h8M335 58v7m-3.5-3.5h7" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
      </g>
      <g className="server-cancellation-ground">
        <path d="M55 172H405M105 182h250" stroke="currentColor" strokeOpacity=".12" />
        <ellipse cx="230" cy="169" rx="96" ry="15" fill="currentColor" fillOpacity=".05" />
        <path d="M164 164v7c0 16 132 16 132 0v-7" fill="currentColor" fillOpacity=".18" />
        <ellipse cx="230" cy="164" rx="66" ry="12" fill="currentColor" fillOpacity=".12" stroke="currentColor" strokeOpacity=".35" />
        <ellipse cx="230" cy="164" rx="47" ry="7" fill="none" stroke="currentColor" strokeOpacity=".4" strokeDasharray="4 6" />
        <path d="M220 164h20m-10-3v6" stroke="currentColor" strokeOpacity=".35" />
        <g className="server-cancellation-pad-lights"><circle cx="167" cy="163" r="2" /><circle cx="293" cy="163" r="2" /><circle cx="230" cy="175" r="2" /></g>
      </g>
      <g className="server-cancellation-rocket server-cancellation-motion">
        <path className="server-cancellation-thrust server-cancellation-motion" d="M218 140Q218 160 230 174Q242 160 242 140Z" fill={`url(#${id}-flame)`} />
        <path d="M213 103L194 125v21l23-12M247 103l19 22v21l-23-12" fill={`url(#${id}-fin)`} stroke="#a28ad0" strokeWidth="1" />
        <path d="M217 136l-13 28m39-28 13 28M199 166h10m42 0h10" stroke="#9f91bb" strokeWidth="3.5" strokeLinecap="round" />
        <path d="M230 48C213 62 208 86 210 119l4 21h32l4-21c2-33-3-57-20-71Z" fill={`url(#${id}-body)`} stroke="#c8b9e8" strokeWidth="1.3" />
        <path d="M230 48c-10 9-16 20-19 34 12 5 26 5 38 0-3-14-9-25-19-34Z" fill={`url(#${id}-fin)`} />
        <circle cx="230" cy="103" r="12.5" fill="#9f87c4" />
        <circle cx="230" cy="103" r="9.5" fill="#29243d" stroke="#ece4ff" strokeWidth="1.5" />
        <path d="M225 101a6 6 0 0 1 6-4" fill="none" stroke="#d6c5ff" strokeWidth="2" strokeLinecap="round" />
        <path d="M224 123h12m-10 4h8" stroke="#b2a1ce" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M215 140h30l-3 5h-24Z" fill="#79658f" />
      </g>
    </svg>
  </div>
}

function ProgressStep({ label, detail, checked, active = false }: { label: string; detail: string; checked: boolean; active?: boolean }) {
  return <li className="server-cancellation-step" data-complete={checked}>
    <span role="checkbox" aria-checked={checked} aria-readonly="true" aria-label={label} className="server-cancellation-step-check">
      {checked ? <CheckIcon aria-hidden="true" className="h-3.5 w-3.5" />
        : active ? <span className="server-cancellation-step-pulse server-cancellation-motion" aria-hidden="true" /> : null}
    </span>
    <span className="min-w-0 flex-1"><span className="server-cancellation-step-label">{label}</span><span className="server-cancellation-step-detail">{detail}</span></span>
  </li>
}

export default function ServerCancellationProgressModal({ serverName, state, receipt, statusUnavailable = false, onClose, onRetry }: ServerCancellationProgressModalProps) {
  const titleId = useId()
  const descriptionId = useId()
  // The receipt, never animation timing or a parent state label, proves acceptance.
  const accepted = receipt?.accepted === true && receipt.status === 'cancelled'
  const submitting = !accepted && state === 'submitting'
  const cleanupComplete = accepted && receipt.cleanup_status === 'completed'
  const cleanupPhase = accepted ? receipt.cleanup_phase : undefined
  const phaseIndex = ['freezing', 'preserving', 'deleting_archives', 'deleting_server', 'cleanup', 'completed'].indexOf(cleanupPhase || '')
  const preserved = cleanupComplete || phaseIndex >= 2
  const archivesDeleted = cleanupComplete || phaseIndex >= 3
  const serverRemoved = cleanupComplete || phaseIndex >= 4
  const { dialogRef, onKeyDown } = useDialogFocus(true, () => { if (!submitting) onClose() })
  const title = accepted ? 'Server cancelled' : submitting ? 'Bringing your server down' : 'Let’s confirm your cancellation'
  const description = accepted
    ? 'No additional server usage or renewal charges will accrue after your cancellation.'
    : submitting ? 'Recording your cancellation and stopping future server billing.'
      : 'We haven’t received confirmation yet. Check the saved status before trying again.'

  return createPortal(<div className="server-cancellation-overlay" onKeyDown={event => event.stopPropagation()}>
    <section ref={element => { dialogRef.current = element }} tabIndex={-1} onKeyDown={onKeyDown}
      role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={descriptionId}
      className="server-cancellation-modal" data-state={accepted ? 'accepted' : submitting ? 'submitting' : 'unconfirmed'}>
      <LandingScene accepted={accepted} submitting={submitting} />
      <div className="server-cancellation-content">
        <p className="server-cancellation-server" title={serverName}>{serverName}</p>
        <div aria-live="polite" aria-atomic="true">
          <h2 id={titleId} className="server-cancellation-title">{title}</h2>
          <p id={descriptionId} className="server-cancellation-description">{description}</p>
        </div>
        <ol className="server-cancellation-steps" aria-label="Cancellation progress" aria-live="polite">
          <ProgressStep label="Cancellation recorded" checked={accepted} active={submitting}
            detail={accepted ? 'Your request is saved.' : submitting ? 'Waiting for confirmation' : 'Not yet confirmed'} />
          <ProgressStep label="Server billing stopped" checked={accepted}
            detail={accepted ? 'No further server usage or renewals.' : 'Waiting for recorded cancellation'} />
          {cleanupPhase ? <>
            <ProgressStep label="Protected backups preserved" checked={preserved} active={cleanupPhase === 'preserving'}
              detail={preserved ? 'Protected backups remain in Backup Vault.' : cleanupPhase === 'preserving' ? 'Preserving protected backups' : 'Pending background cleanup'} />
            <ProgressStep label="Ordinary backups deleted" checked={archivesDeleted} active={cleanupPhase === 'deleting_archives'}
              detail={archivesDeleted ? 'Ordinary backups removed.' : cleanupPhase === 'deleting_archives' ? 'Removing ordinary backups' : 'Pending background cleanup'} />
            <ProgressStep label="Server removed" checked={serverRemoved} active={cleanupPhase === 'deleting_server'}
              detail={serverRemoved ? 'Server removal confirmed.' : cleanupPhase === 'deleting_server' ? 'Removing the server' : 'Pending background cleanup'} />
            <ProgressStep label="Final cleanup" checked={cleanupComplete} active={cleanupPhase === 'cleanup' && !cleanupComplete}
              detail={cleanupComplete ? 'Server cleanup is complete.' : 'Continuing in background'} />
          </> : <ProgressStep label="Background cleanup" checked={cleanupComplete}
            detail={cleanupComplete ? 'Server cleanup is complete.' : accepted ? 'Continuing in background' : 'Pending cancellation'} />}
        </ol>
        {accepted && receipt && <div className="server-cancellation-receipt">
          <span>Billing stopped</span>
          <time dateTime={receipt.billing_cutoff_at}>{new Date(receipt.billing_cutoff_at).toLocaleString()}</time>
        </div>}
        {statusUnavailable && <p role="status" className="server-cancellation-status-note">{accepted
          ? 'Cleanup status is temporarily unavailable. Your cancellation and billing cutoff are confirmed.'
          : 'Cancellation status is temporarily unavailable. We have not confirmed a billing cutoff yet.'}</p>}
        {accepted && !cleanupComplete && <p className="server-cancellation-background-note">You can close this window. We’ll finish cleanup in the background.</p>}
        <div className="server-cancellation-actions">
          {accepted ? <button type="button" className="server-cancellation-primary" onClick={onClose}>Done</button>
            : submitting ? <button type="button" className="server-cancellation-primary" disabled><ArrowPathIcon aria-hidden="true" className="server-cancellation-spinner server-cancellation-motion" />Recording cancellation…</button>
              : <><button type="button" className="server-cancellation-secondary" onClick={onClose}>Close</button><button type="button" className="server-cancellation-primary" onClick={onRetry}>Check cancellation</button></>}
        </div>
        <p className="server-cancellation-fine-print">Previously incurred charges still apply. Protected backups stay in Backup Vault, where storage is billed separately.</p>
      </div>
    </section>
  </div>, document.body)
}
